import { useCallback } from 'react';

import { usePico } from '../pico';

/**
 * Search results page tracking props - matches vimeo.search_results_page BP event
 */
export type SearchResultsPageProps = {
  searchId: string;
  searchText: string | null;
  resultsPageNumber: number | null;
  orderBy: string | null;
  resultsQty: number | null;
  filters: string[] | null;
  filterValues: (string | number)[] | null;
  searchType: 'library' | 'public';
};

/**
 * Search result hover tracking props - matches vimeo.search_result_hover BP event
 */
export type SearchResultHoverProps = {
  searchId: string;
  searchText: string | null;
  defaultSearchType: string;
  resultsPageNumber: number | null;
  resultPosition: number;
  entityId: number | null;
  entityType: string;
  orderBy: string | null;
  resultsQty: number | null;
  isAiRequest: boolean | null;
};

/**
 * Search result click tracking props - matches vimeo.search_result_click BP event
 */
export type SearchResultClickProps = {
  searchId: string;
  searchText: string | null;
  defaultSearchType: string;
  resultsPageNumber?: number | null;
  resultPosition: number;
  entityId: number | null;
  entityType: string;
  orderBy: string | null;
  resultsQty: number | null;
  isAiRequest: boolean | null;
};

/**
 * Search watch later tracking props - matches vimeo.search_watch_later BP event
 */
export type SearchWatchLaterProps = {
  searchId: string;
  searchText: string | null;
  defaultSearchType: string;
  resultsPageNumber: number | null;
  resultPosition: number;
  entityId: number | null;
  entityType: string;
  orderBy: string | null;
  resultsQty: number | null;
};

/**
 * Search filter tracking props - matches vimeo.search_filter BP event
 */
export type SearchFilterProps = {
  searchId: string;
  searchText: string | null;
  defaultSearchType: string;
  resultsPageNumber: number | null;
  previousValue: string[] | null;
  value: string[] | null;
  valueName: string;
  orderBy: string | null;
  resultsQty: number | null;
  isAiRequest: boolean | null;
};

/**
 * Search sorting tracking props - matches vimeo.search_sorting BP event
 */
export type SearchSortingProps = {
  searchId: string;
  searchText: string | null;
  defaultSearchType: string;
  resultsPageNumber: number | null;
  previousValue: string[];
  value: string[];
  valueName: string;
  orderBy: string;
  resultsQty: number | null;
  isAiRequest: boolean | null;
};

/**
 * Search mode switch tracking props - matches vimeo.search_mode_switch BP event
 */
export type SearchModeSwitchProps = {
  searchId: string;
  searchText: string | null;
  defaultSearchType: string;
  resultsQty: number | null;
  copy: string;
};

/**
 * Notification view tracking props - matches vimeo.notification_view BP event
 */
export type NotificationViewProps = {
  notificationName: string;
  notificationCopy: string;
};

/**
 * Notification action tracking props - matches vimeo.notification_action BP event
 */
export type NotificationActionProps = {
  notificationName: string;
  notificationCopy: string;
};

/**
 * View generate AI search tracking props - matches vimeo.view_generate_ai_search BP event
 */
export type ViewGenerateAiSearchProps = Record<string, never>;

/**
 * View suggested AI search tracking props - matches vimeo.view_suggested_ai_search BP event
 */
export type ViewSuggestedAiSearchProps = {
  videoId: number | null;
  errorValue: string | null;
};

/**
 * Select suggested AI search tracking props - matches vimeo.select_suggested_ai_search BP event
 */
export type SelectSuggestedAiSearchProps = {
  entityType: 'video' | 'moment';
  videoId: number | null;
};

/**
 * AI search widget actions tracking props - matches vimeo.ai_search_widget_actions BP event
 */
export type AiSearchWidgetActionsProps = {
  copy: string;
  videoId: number | null;
};

/**
 * Thumbs rate tracking props - matches vimeo.thumbs_rate BP event
 */
export type ThumbsRateProps = {
  ratedFeature: string;
  isPositive: boolean;
  videoId?: number | null;
  generationId?: string | null;
};

/**
 * Moments timeline click tracking props - matches vimeo.moments_timeline_clicks BP event
 */
export type MomentsTimelineClickProps = {
  searchId: string;
  resultsPageNumber: number | null;
  resultPosition: number;
  entityId: number | null;
};

/**
 * Moments panel click tracking props - matches vimeo.moments_panel_clicks_interactions BP event
 */
export type MomentsPanelClickProps = {
  searchId: string;
  resultsPageNumber: number | null;
  entityId: number | null;
  valueName: string;
};

/**
 * Moments impressions tracking props - matches vimeo.moments_*_impressions BP events
 */
export type MomentsImpressionsProps = {
  searchId: string;
  resultsPageNumber: number | null;
};

/**
 * Search page displayed tracking props
 */
export type SearchPageDisplayedProps = {
  searchQuery: string;
  searchActiveTab: 'vimeo' | 'library';
  searchResultType: string;
  searchPageViewerAuthStatus: string;
};

/**
 * Hook for tracking search events in PicoX.
 *
 * Provides methods to track all search-related events that match BP events.
 */
export const useSearchTracking = () => {
  const pico = usePico();

  /**
   * Track search results page event - emulates vimeo.search_results_page BP event
   */
  const trackSearchResultsPage = useCallback(
    (props: SearchResultsPageProps) => {
      if (pico === null) {
        return;
      }
      pico.track('search_results_page', {
        search_id: props.searchId,
        search_text: props.searchText,
        results_page_number: props.resultsPageNumber,
        order_by: props.orderBy,
        results_qty: props.resultsQty,
        filters: props.filters,
        filter_values: props.filterValues,
        search_type: props.searchType,
      });
    },
    [pico],
  );

  /**
   * Track search result hover event - emulates vimeo.search_result_hover BP event
   */
  const trackSearchResultHover = useCallback(
    (props: SearchResultHoverProps) => {
      if (pico === null) {
        return;
      }
      pico.track('search_result_hover', {
        search_id: props.searchId,
        search_text: props.searchText,
        default_search_type: props.defaultSearchType,
        results_page_number: props.resultsPageNumber,
        result_position: props.resultPosition,
        entity_id: props.entityId,
        entity_type: props.entityType,
        order_by: props.orderBy,
        results_qty: props.resultsQty,
        is_ai_request: props.isAiRequest,
      });
    },
    [pico],
  );

  /**
   * Track search result click event - emulates vimeo.search_result_click BP event
   */
  const trackSearchResultClick = useCallback(
    (props: SearchResultClickProps) => {
      if (pico === null) {
        return;
      }
      pico.track('search_result_click', {
        search_id: props.searchId,
        search_text: props.searchText,
        default_search_type: props.defaultSearchType,
        results_page_number: props.resultsPageNumber ?? null,
        result_position: props.resultPosition,
        entity_id: props.entityId,
        entity_type: props.entityType,
        order_by: props.orderBy,
        results_qty: props.resultsQty,
        is_ai_request: props.isAiRequest,
      });
    },
    [pico],
  );

  /**
   * Track search watch later event - emulates vimeo.search_watch_later BP event
   */
  const trackSearchWatchLater = useCallback(
    (props: SearchWatchLaterProps) => {
      if (pico === null) {
        return;
      }
      pico.track('search_watch_later', {
        search_id: props.searchId,
        search_text: props.searchText,
        default_search_type: props.defaultSearchType,
        results_page_number: props.resultsPageNumber,
        result_position: props.resultPosition,
        entity_id: props.entityId,
        entity_type: props.entityType,
        order_by: props.orderBy,
        results_qty: props.resultsQty,
      });
    },
    [pico],
  );

  /**
   * Track search filter event - emulates vimeo.search_filter BP event
   */
  const trackSearchFilter = useCallback(
    (props: SearchFilterProps) => {
      if (pico === null) {
        return;
      }
      pico.track('search_filter', {
        search_id: props.searchId,
        search_text: props.searchText,
        default_search_type: props.defaultSearchType,
        results_page_number: props.resultsPageNumber,
        previous_value: props.previousValue,
        value: props.value,
        value_name: props.valueName,
        order_by: props.orderBy,
        results_qty: props.resultsQty,
        is_ai_request: props.isAiRequest,
      });
    },
    [pico],
  );

  /**
   * Track search sorting event - emulates vimeo.search_sorting BP event
   */
  const trackSearchSorting = useCallback(
    (props: SearchSortingProps) => {
      if (pico === null) {
        return;
      }
      pico.track('search_sorting', {
        search_id: props.searchId,
        search_text: props.searchText,
        default_search_type: props.defaultSearchType,
        results_page_number: props.resultsPageNumber,
        previous_value: props.previousValue,
        value: props.value,
        value_name: props.valueName,
        order_by: props.orderBy,
        results_qty: props.resultsQty,
        is_ai_request: props.isAiRequest,
      });
    },
    [pico],
  );

  /**
   * Track search mode switch event - emulates vimeo.search_mode_switch BP event
   */
  const trackSearchModeSwitch = useCallback(
    (props: SearchModeSwitchProps) => {
      if (pico === null) {
        return;
      }
      pico.track('search_mode_switch', {
        search_id: props.searchId,
        search_text: props.searchText,
        default_search_type: props.defaultSearchType,
        results_qty: props.resultsQty,
        copy: props.copy,
      });
    },
    [pico],
  );

  /**
   * Track notification view event - emulates vimeo.notification_view BP event
   */
  const trackNotificationView = useCallback(
    (props: NotificationViewProps) => {
      if (pico === null) {
        return;
      }
      pico.track('notification_view', {
        notification_name: props.notificationName,
        notification_copy: props.notificationCopy,
      });
    },
    [pico],
  );

  /**
   * Track notification action event - emulates vimeo.notification_action BP event
   */
  const trackNotificationAction = useCallback(
    (props: NotificationActionProps) => {
      if (pico === null) {
        return;
      }
      pico.track('notification_action', {
        notification_name: props.notificationName,
        notification_copy: props.notificationCopy,
      });
    },
    [pico],
  );

  /**
   * Track view generate AI search event - emulates vimeo.view_generate_ai_search BP event
   */
  const trackViewGenerateAiSearch = useCallback(
    (_props?: ViewGenerateAiSearchProps) => {
      if (pico === null) {
        return;
      }
      pico.track('view_generate_ai_search', {});
    },
    [pico],
  );

  /**
   * Track view suggested AI search event - emulates vimeo.view_suggested_ai_search BP event
   */
  const trackViewSuggestedAiSearch = useCallback(
    (props: ViewSuggestedAiSearchProps) => {
      if (pico === null) {
        return;
      }
      pico.track('view_suggested_ai_search', {
        video_id: props.videoId,
        error_value: props.errorValue,
      });
    },
    [pico],
  );

  /**
   * Track select suggested AI search event - emulates vimeo.select_suggested_ai_search BP event
   */
  const trackSelectSuggestedAiSearch = useCallback(
    (props: SelectSuggestedAiSearchProps) => {
      if (pico === null) {
        return;
      }
      pico.track('select_suggested_ai_search', {
        entity_type: props.entityType,
        video_id: props.videoId,
      });
    },
    [pico],
  );

  /**
   * Track AI search widget actions event - emulates vimeo.ai_search_widget_actions BP event
   */
  const trackAiSearchWidgetActions = useCallback(
    (props: AiSearchWidgetActionsProps) => {
      if (pico === null) {
        return;
      }
      pico.track('ai_search_widget_actions', {
        copy: props.copy,
        video_id: props.videoId,
      });
    },
    [pico],
  );

  /**
   * Track thumbs rate event - emulates vimeo.thumbs_rate BP event
   */
  const trackThumbsRate = useCallback(
    (props: ThumbsRateProps) => {
      if (pico === null) {
        return;
      }
      pico.track('thumbs_rate', {
        rated_feature: props.ratedFeature,
        is_positive: props.isPositive,
        video_id: props.videoId ?? null,
        generation_id: props.generationId ?? null,
      });
    },
    [pico],
  );

  /**
   * Track moments timeline click event - emulates vimeo.moments_timeline_clicks BP event
   */
  const trackMomentsTimelineClick = useCallback(
    (props: MomentsTimelineClickProps) => {
      if (pico === null) {
        return;
      }
      pico.track('moments_timeline_clicks', {
        search_id: props.searchId,
        results_page_number: props.resultsPageNumber,
        result_position: props.resultPosition,
        entity_id: props.entityId,
      });
    },
    [pico],
  );

  /**
   * Track moments panel click event - emulates vimeo.moments_panel_clicks_interactions BP event
   */
  const trackMomentsPanelClick = useCallback(
    (props: MomentsPanelClickProps) => {
      if (pico === null) {
        return;
      }
      pico.track('moments_panel_clicks_interactions', {
        search_id: props.searchId,
        results_page_number: props.resultsPageNumber,
        entity_id: props.entityId,
        value_name: props.valueName,
      });
    },
    [pico],
  );

  /**
   * Track moments results impressions event - emulates vimeo.moments_results_impressions BP event
   */
  const trackMomentsResultsImpressions = useCallback(
    (props: MomentsImpressionsProps) => {
      if (pico === null) {
        return;
      }
      pico.track('moments_results_impressions', {
        search_id: props.searchId,
        results_page_number: props.resultsPageNumber,
      });
    },
    [pico],
  );

  /**
   * Track moments timeline impressions event - emulates vimeo.moments_timeline_impressions BP event
   */
  const trackMomentsTimelineImpressions = useCallback(
    (props: MomentsImpressionsProps) => {
      if (pico === null) {
        return;
      }
      pico.track('moments_timeline_impressions', {
        search_id: props.searchId,
        results_page_number: props.resultsPageNumber,
      });
    },
    [pico],
  );

  /**
   * Track moments panel impressions event - emulates vimeo.moments_panel_impressions BP event
   */
  const trackMomentsPanelImpressions = useCallback(
    (props: MomentsImpressionsProps) => {
      if (pico === null) {
        return;
      }
      pico.track('moments_panel_impressions', {
        search_id: props.searchId,
        results_page_number: props.resultsPageNumber,
      });
    },
    [pico],
  );

  const trackSearchPageDisplayed = useCallback(
    (props: SearchPageDisplayedProps) => {
      if (pico === null) {
        return false;
      }
      pico.track('search_page_displayed', {
        search_query: props.searchQuery,
        search_active_tab: props.searchActiveTab,
        search_result_type: props.searchResultType,
        search_page_viewer_auth_status: props.searchPageViewerAuthStatus,
      });
      return true;
    },
    [pico],
  );

  return {
    trackSearchPageDisplayed,
    trackSearchResultsPage,
    trackSearchResultHover,
    trackSearchResultClick,
    trackSearchWatchLater,
    trackSearchFilter,
    trackSearchSorting,
    trackSearchModeSwitch,
    trackNotificationView,
    trackNotificationAction,
    trackViewGenerateAiSearch,
    trackViewSuggestedAiSearch,
    trackSelectSuggestedAiSearch,
    trackAiSearchWidgetActions,
    trackThumbsRate,
    trackMomentsTimelineClick,
    trackMomentsPanelClick,
    trackMomentsResultsImpressions,
    trackMomentsTimelineImpressions,
    trackMomentsPanelImpressions,
  };
};
